import type { FlairPart, Glyph } from '../../flair/engine';
import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part } from '../body';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';
import { type HeadPicture, mask, type SetFlair } from './mythic';

/**
 * The Knight set (ADR-0160 amended, mythic): a crowned champion of the lists. A great helm under a gold coronet, plate
 * and a tabard in the team's colour, a banner flying from a pole on its back, and the Arbalest.
 *
 * The head is a closed great helm: a steel drum with a flat top (its plate catching the light), lit from the left and
 * going dark round its far side, a riveted rim at its foot; a gold cross riveted down its front, the eye slit across it
 * dark (the team's light glows in it: flair), breaths punched in its cheek; and round its top a gold coronet, four
 * points standing up off it with a jewel in the team's colour in its band under each. Its flair: the slit's glow, a glint
 * running round the coronet.
 * The suit is plate and a tabard: steel pauldrons on the arms (lames lit along their tops), a tabard in the team's colour
 * over mail (the mail shows at the sides and the throat), a gold cross on it, a brown belt and gold buckle; steel
 * sabatons. Its flair: a banner in the team's colour flying back off a pole on its back, a gold finial on top, fluttering
 * and lagging its moves (the bright moving part at 1:1, idle too: it never stops fluttering).
 *
 * Rubric (`assetc mythic knight` at 5× and 1:1 on grass, lane and night, both teams, idle, run and dead, beside the
 * Chronomancer): head S4 R4 P4 L4 C4 A4 F4, plate S4 R4 P4 L4 C4 F4. Weakest: the helm is steel on a steel gun; the
 * coronet's gold and the slit's glow part them.
 */

// --- The great helm --------------------------------------------------------------------------------------------------

/** The drum's sides (u), its top and its foot (v) standing; lower on the dead pose, whose face is bigger. */
const [LEFT, RIGHT, TOP] = [-20.5, 20.5, -15] as const;
const footOf = (lying: boolean) => (lying ? 20 : 17);
/** The gold cross down its front: its column, and the eye slit across it. */
const CROSS = [8.5, 12] as const;
const SLIT = [0.5, 3.5] as const;
/** The coronet's band, and its points (u of each, rising to v -22). */
const BAND = [-15, -11] as const;
const POINTS = [-15, -5, 5, 15] as const;

/** Steel at light `l` (1 lit … 0 dark). */
const steel = (l: number): PaletteKey => (l > 0.85 ? 'w' : l > 0.62 ? '5' : l > 0.38 ? '4' : l > 0.16 ? '3' : '2');

const helm: HeadPicture = (u, v, _lx, _ly, lying) => {
  const foot = footOf(lying);
  // The coronet's points: gold triangles off its band, lit on their left.
  for (const p of POINTS) {
    const h = 6.5 - Math.abs(u - p) * 1.9;
    if (v < BAND[0] && v >= BAND[0] - h && h > 0) {
      if (Math.hypot(u - p, v - (BAND[0] - 6.2)) < 1.1) return 'y';
      return u < p ? 'y' : u - p > 1 ? 'U' : 'Y';
    }
  }
  // Round its corners: the drum's outline.
  const corner = 4;
  const cx = Math.max(LEFT + corner - u, u - (RIGHT - corner), 0);
  const cy = Math.max(TOP + corner - v, v - (foot - 2), 0);
  if (u < LEFT || u > RIGHT || v < TOP || v > foot || Math.hypot(cx, cy) > corner) return null;
  const across = (u - LEFT) / (RIGHT - LEFT);
  // The coronet's band: gold, a jewel in the team's colour under each point.
  if (v >= BAND[0] && v < BAND[1]) {
    for (const p of POINTS)
      if (Math.abs(u - p) < 1.6 && v > BAND[0] + 0.6 && v < BAND[1] - 0.6)
        return u < p && v < BAND[0] + 2 ? 'c' : u > p ? 'B' : 'b';
    if (v < BAND[0] + 1) return across < 0.7 ? 'y' : 'Y';
    if (v > BAND[1] - 1) return 'U';
    return across < 0.35 ? 'y' : across < 0.8 ? 'Y' : 'U';
  }
  // The flat top's plate, lit, its near rim catching the light.
  if (v < BAND[0]) return v < TOP + 1 ? '4' : across < 0.45 ? 'w' : '5';
  // The rim round its foot: darker steel, rivets.
  if (v > foot - 3) {
    if (v < foot - 1.8 && Math.floor(u + 40) % 6 === 2) return '5';
    return v > foot - 1 ? '2' : across > 0.8 ? '2' : '3';
  }
  // The eye slit across the front, dark (its glow is flair), broken by the cross.
  const slit = v >= SLIT[0] && v < SLIT[1] && u > 1 && u < RIGHT - 0.5;
  // The gold cross down its front, riveted, lit on its left.
  if (u >= CROSS[0] && u < CROSS[1] && !(slit && v > SLIT[0] + 0.5 && v < SLIT[1] - 0.5)) {
    if (Math.floor(v + 40) % 7 === 3) return 'w';
    return u < CROSS[0] + 1 ? 'y' : u > CROSS[1] - 1 ? 'U' : 'Y';
  }
  if (slit) return v < SLIT[0] + 1 ? '1' : 'k';
  // Breaths punched in the cheek, under the slit.
  if (u > 13.5 && u < RIGHT - 2 && v > 6 && v < 12 && Math.floor(u) % 3 === 0 && Math.floor(v) % 3 === 0) return 'k';
  // The steel drum: lit from the left, its far side in shade; a soft highlight down its lit side.
  const l = 0.85 - across * 0.95 + (Math.abs(across - 0.16) < 0.05 ? 0.2 : 0);
  return steel(across > 0.94 ? 0.1 : l);
};

// --- Plate and tabard ------------------------------------------------------------------------------------------------

/** The suit standing (the frame's x 5-56, y 39-57): pauldrons, the team's tabard over mail, a gold cross, a belt. */
function plate(): Part {
  const [x0, y0] = [5, 39];
  const c = new PixelCanvas(52, 19);
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  const inSuit = (x: number, y: number) => {
    if (y < 41 || y > 57) return false;
    if (x >= 16 && x <= 43) return true;
    for (const [cx, rx] of [
      [12.5, 7.5],
      [47, 6.5],
    ] as const) {
      const [dx, dy] = [(x + 0.5 - cx) / rx, (y + 0.5 - 47) / 5.8];
      if (dx * dx + dy * dy <= 1) return true;
    }
    return false;
  };
  const arm = (x: number) => x < 16 || x > 43;
  for (let y = 41; y <= 57; y++)
    for (let x = 5; x <= 56; x++) {
      if (!inSuit(x, y)) continue;
      const lit = !inSuit(x - 1, y) || !inSuit(x, y - 1);
      const edge = !inSuit(x + 1, y);
      if (arm(x)) {
        // Pauldrons: steel lames, each lit along its top, darker on the far arm.
        const lame = (y - 41) % 3;
        const far = x > 43;
        at(x, y, edge ? '2' : lit || lame === 0 ? (far ? '4' : '5') : lame === 2 ? '3' : far ? '3' : '4');
        continue;
      }
      // Mail: rings in a checker, at the sides and the throat.
      at(x, y, (x + y) % 2 ? '3' : lit ? '5' : '4');
    }
  // The tabard in the team's colour, from the throat to the hem, its near edge lit, its far side in shade.
  for (let y = 43; y <= 57; y++)
    for (let x = 19; x <= 40; x++) {
      const near = x === 19;
      const far = x >= 38;
      at(x, y, near ? 'c' : far ? 'B' : y === 57 ? 'B' : 'b');
    }
  // Its gold cross: long down the middle, short across the chest.
  for (let y = 44; y <= 56; y++) {
    if (y === 53 || y === 54) continue;
    at(28, y, 'y');
    at(29, y, 'Y');
    at(30, y, 'U');
  }
  for (let x = 23; x <= 35; x++) {
    at(x, 47, x < 29 ? 'y' : 'Y');
    at(x, 48, 'U');
  }
  // The neck's mail over it, a collar of steel.
  for (let x = 18; x <= 41; x++) {
    at(x, 41, x < 22 ? '5' : '4');
    at(x, 42, x > 37 ? '2' : '3');
  }
  // A brown belt with a gold buckle.
  for (let x = 16; x <= 43; x++) {
    at(x, 53, x > 40 ? 'N' : 'm');
    at(x, 54, x > 40 ? 'N' : 'n');
  }
  c.stamp(
    `
    yYYU
    Y..U
  `,
    27 - x0,
    53 - y0,
  );
  return [c, x0, y0];
}

/** The suit lying (right of the legs, under the head): the belt and hem (left), the tabard and its cross, the collar. */
const DEAD_PLATE = grid(`
  ....4444
  ...45554
  1n2bbbb3
  kU2cbbb4
  1n2bbbb3
  kn2bbYb4
  1n2bbYb3
  kn2bbYb4
  1nyYYYY3
  kn2bbYb4
  1n2bbYb3
  kn2bbYb4
  1n2bbbb3
  kU2bbbb4
  1n2bbbb3
  kn2BBBB4
  k1111113
  ....3333
`);

/** Steel sabatons. */
const PLATE = (): Outfit => ({ torso: plate(), dead: [DEAD_PLATE, 24, 51], legs: { n: '3', N: '2', m: '4' } });

// --- The Arbalest's steel and walnut on every other gun --------------------------------------------------------------

/** Burnished steel (the default gun is its own, `pollinator_knight`): the steel a step brighter. */
const BURNISHED = { '3': '4', '4': '5', '5': 'w' } as const;

// --- Its flair -------------------------------------------------------------------------------------------------------

const WHITE = 0xffffff;
const YELLOW = 0xfee761;
const GOLD = 0xfeae34;
const DARK = 0x181425;
const WOOD = 0x733e39;

/** The banner's pole and the pennant fly from its top (the frame's (6, 21), behind the head's back). */
const POLE_TOP = { on: 'torso', x: 3, y: 21 } as const;

/** The pole's gold finial: a spear tip over a knob. */
const FINIAL: Glyph = {
  kind: 'glyph',
  at: { on: 'torso', x: 3 - 2, y: 21 - 7 },
  frames: [
    `
    ..y..
    .yyY.
    .yYY.
    ..Y..
    .YYU.
    ..U..
  `,
  ],
  tones: { y: YELLOW, Y: GOLD, U: 0xd77643 },
  hold: [1, 1],
  layer: 'back',
};

const pole: FlairPart = {
  kind: 'chain',
  at: POLE_TOP,
  n: 11,
  seg: 2,
  dir: Math.PI / 2,
  stiff: 60,
  g: 0,
  drag: 0.4,
  w: [3, 3, 3],
  shape: 'dots',
  tones: [WOOD],
  rim: DARK,
  lit: 0xb86f50,
  layer: 'back',
};

const FLAIR: SetFlair = {
  head: [
    // The team's light in the eye slit.
    {
      kind: 'pulse',
      at: { on: 'head', x: 44, y: 30 },
      r: [3, 4.5],
      alpha: [0.35, 0.6],
      period: 1.8,
      tone: 'teamLight',
      layer: 'front',
    },
    // A glint running round the coronet.
    {
      kind: 'twinkle',
      at: { on: 'head', x: 30, y: 10 },
      box: [32, 6],
      n: 1,
      period: [0.6, 1.1],
      tones: [WHITE, YELLOW],
      add: true,
      layer: 'front',
    },
  ],
  outfit: [
    pole,
    FINIAL,
    // The banner: the team's colour, a gold stripe near the pole, flying back and fluttering.
    {
      kind: 'chain',
      at: { on: 'torso', x: 3, y: 25 },
      n: 9,
      seg: 2,
      dir: Math.PI * 0.94,
      curl: 0.02,
      stiff: 9,
      g: 60,
      drag: 0.06,
      sway: [0.12, 0.7, 0],
      wind: 1800,
      w: [8, 7, 3],
      shape: 'bars',
      tones: ['team', GOLD, 'team', 'team', 'team', 'team', 'teamLight'],
      rim: 'teamDark',
      lit: 'teamLight',
      tatter: 2,
      layer: 'back',
    },
    // A glint on the cross.
    {
      kind: 'twinkle',
      at: { on: 'torso', x: 29, y: 49 },
      box: [10, 10],
      n: 1,
      period: [0.9, 1.5],
      tones: [WHITE, YELLOW],
      add: true,
      layer: 'front',
    },
  ],
  gun: [
    // The bolt's gold head glowing at the muzzle, a glint on it.
    {
      kind: 'pulse',
      at: { on: 'gun', x: 60, y: 20 },
      r: [2, 3.5],
      alpha: [0.25, 0.45],
      period: 1.1,
      tone: GOLD,
      layer: 'gun',
    },
    {
      kind: 'twinkle',
      at: { on: 'gun', x: 46, y: 20 },
      box: [6, 26],
      n: 1,
      period: [0.7, 1.2],
      tones: [WHITE, YELLOW],
      add: true,
      layer: 'gun',
    },
  ],
};

/** The Knight set (ADR-0160 amended). */
export const KNIGHT = drawnLater({ head: () => mask(helm), outfit: PLATE, gun: BURNISHED, flair: FLAIR });
